import { ReactNode } from "react";
import { Download, Search } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export interface TabOption<T extends string = string> {
  key: T;
  label: string;
}

export interface SortOption<T extends string = string> {
  value: T;
  label: string;
}

interface ListToolbarProps<
  TTab extends string = string,
  TSort extends string = string,
> {
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
  };
  tabs?: {
    value: TTab;
    onChange: (value: TTab) => void;
    options: Array<TabOption<TTab>>;
  };
  sort?: {
    value: TSort;
    onChange: (value: TSort) => void;
    options: Array<SortOption<TSort>>;
  };
  filters?: ReactNode;
  actions?: ReactNode;
  onExport?: () => void;
  exportLabel?: string;
}

export const ListToolbar = <
  TTab extends string = string,
  TSort extends string = string,
>({
  search,
  tabs,
  sort,
  filters,
  actions,
  onExport,
  exportLabel = "Export CSV",
}: ListToolbarProps<TTab, TSort>) => {
  const hasTopRow = Boolean(search || actions || onExport);
  const hasBottomRow = Boolean(tabs || sort || filters);

  return (
    <TooltipProvider delayDuration={200}>
      <div className="mb-4 space-y-3">
        {hasTopRow && (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {search && (
                <div className="relative w-full sm:w-64">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    value={search.value}
                    onChange={(e) => search.onChange(e.target.value)}
                    placeholder={search.placeholder ?? "Search..."}
                    className="pl-9 pr-4 py-2 text-sm rounded-btn border border-border bg-card focus:outline-none focus:ring-2 focus:ring-primary/30 w-full"
                  />
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {actions}
              {onExport && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={onExport}
                      aria-label={exportLabel}
                      className="flex items-center justify-center bg-card border border-border rounded-btn p-2 hover:bg-muted transition-colors"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>{exportLabel}</TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        )}

        {hasBottomRow && (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {tabs && (
                <div className="flex gap-1 bg-card rounded-btn border border-border p-1 w-fit flex-wrap">
                  {tabs.options.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => tabs.onChange(t.key)}
                      className={`px-3 py-1.5 text-sm rounded-btn font-medium transition-colors ${
                        tabs.value === t.key
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              )}
              {filters}
            </div>
            {sort && (
              <div className="flex items-center gap-2">
                <label className="text-xs text-muted-foreground">Sort</label>
                <select
                  value={sort.value}
                  onChange={(e) => sort.onChange(e.target.value as TSort)}
                  className="text-sm rounded-btn border border-border bg-card px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {sort.options.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default ListToolbar;
